import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Archive, Copy, Calendar, Loader2, AlertTriangle, CheckCircle2, Send, Trophy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SeasonPanel({ orgId }) {
  const { isDark } = useTheme();
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSeason, setCurrentSeason] = useState('');
  const [showArchive, setShowArchive] = useState(false);
  const [showCopy, setShowCopy] = useState(false);
  const [seasonLabel, setSeasonLabel] = useState('');
  const [newSeason, setNewSeason] = useState('');
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [clearRosters, setClearRosters] = useState(true);

  // Finalize-Season-to-WP state (Session 25, Feb 2026).
  const [showFinalize, setShowFinalize] = useState(false);
  const [sports, setSports] = useState([]);
  const [finalizeSport, setFinalizeSport] = useState('');
  const [finalizeSeason, setFinalizeSeason] = useState('');
  const [finalizePrior, setFinalizePrior] = useState('');
  const [finalizePreview, setFinalizePreview] = useState(null);
  const [finalizeLog, setFinalizeLog] = useState([]);

  const fetchArchives = useCallback(async () => {
    if (!orgId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/organizations/${orgId}/seasons/archives`);
      setArchives(data);
    } catch (err) {
      console.error('Failed to fetch archives:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  // Current active season from the ONE rule (backend /seasons is_current — the
  // org-level school-year flip, snapped to games). No hardcoded year, no
  // divergent month math. Falls back to the newest season only if none flagged.
  const fetchCurrentSeason = useCallback(async () => {
    if (!orgId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/ol-canonical/seasons/${orgId}`);
      const rows = data?.data || [];
      const cur = rows.find(s => s.is_current) || rows[0];
      setCurrentSeason(cur?.slug || '');
    } catch (_) {
      setCurrentSeason('');
    }
  }, [orgId]);

  useEffect(() => { fetchArchives(); fetchCurrentSeason(); }, [fetchArchives, fetchCurrentSeason]);

  // Prefill the season-archive label with the detected current season — AD
  // doesn't have to retype "2025-2026" every time they hit the page.
  useEffect(() => {
    if (currentSeason && !seasonLabel) setSeasonLabel(currentSeason);
  }, [currentSeason, seasonLabel]);

  const handleArchive = async () => {
    if (!seasonLabel.trim()) return;
    setActionLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/organizations/${orgId}/seasons/archive`, {
        season: seasonLabel,
        clear_rosters: clearRosters,
      });
      setResult({ type: 'success', message: `Season "${data.season}" archived. ${data.roster_entries_archived} roster entries saved. Rosters ${data.rosters_cleared ? 'cleared' : 'kept'}.` });
      fetchArchives();
    } catch (err) {
      setResult({ type: 'error', message: err.response?.data?.detail || 'Archive failed' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!selectedArchive || !newSeason.trim()) return;
    setActionLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/organizations/${orgId}/seasons/copy`, {
        archive_id: selectedArchive,
        new_season: newSeason,
      });
      setResult({ type: 'success', message: data.message });
    } catch (err) {
      setResult({ type: 'error', message: err.response?.data?.detail || 'Copy failed' });
    } finally {
      setActionLoading(false);
    }
  };

  // ──── Finalize Season to WordPress (AD-direct) ────
  // Pulls the AD's sport list, lets them pick the new active season +
  // prior season to wipe, previews counts, then calls
  // /api/season-lifecycle/finalize-sport/{org_id} on confirm. WP is
  // updated via the standard outbox/agent path.
  const fetchSportsAndLog = useCallback(async () => {
    if (!orgId) return;
    try {
      const [{ data: sportsData }, { data: logData }] = await Promise.all([
        axios.get(`${API_URL}/api/organizations/${orgId}/sports`),
        axios.get(`${API_URL}/api/season-lifecycle/finalize-sport/${orgId}/log`),
      ]);
      setSports(sportsData || []);
      setFinalizeLog(logData?.data || []);
    } catch (_) { /* AD may not have permission to finalize — that's OK */ }
  }, [orgId]);

  useEffect(() => { fetchSportsAndLog(); }, [fetchSportsAndLog]);

  const fetchFinalizePreview = useCallback(async () => {
    if (!finalizeSport || !finalizeSeason.trim()) { setFinalizePreview(null); return; }
    try {
      const params = new URLSearchParams({
        sport_id: finalizeSport,
        season_slug: finalizeSeason,
      });
      if (finalizePrior.trim()) params.set('prior_season_slug', finalizePrior);
      const { data } = await axios.get(
        `${API_URL}/api/season-lifecycle/finalize-sport/${orgId}/preview?${params.toString()}`
      );
      setFinalizePreview(data);
    } catch (err) {
      setFinalizePreview({ error: err.response?.data?.detail || 'Preview failed' });
    }
  }, [orgId, finalizeSport, finalizeSeason, finalizePrior]);

  useEffect(() => { fetchFinalizePreview(); }, [fetchFinalizePreview]);

  const handleFinalize = async () => {
    if (!finalizeSport || !finalizeSeason.trim()) return;
    setActionLoading(true);
    setResult(null);
    try {
      const payload = {
        sport_id: finalizeSport,
        season_slug: finalizeSeason,
      };
      if (finalizePrior.trim()) payload.prior_season_slug = finalizePrior;
      const { data } = await axios.post(
        `${API_URL}/api/season-lifecycle/finalize-sport/${orgId}`, payload
      );
      setResult({
        type: data.state === 'succeeded' ? 'success' : 'error',
        message: `Finalize ${data.state}: published ${data.published_players} player(s), wiped ${data.wiped_players} prior-season row(s) on WP across ${data.team_count} team(s).`,
      });
      fetchSportsAndLog();
    } catch (err) {
      setResult({ type: 'error', message: err.response?.data?.detail || 'Finalize failed' });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="season-panel">
      <Card className={isDark ? 'bg-slate-800/40 border-slate-700' : 'bg-slate-50 border-slate-200'}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-amber-500/15 flex-shrink-0">
              <Calendar className="h-5 w-5 text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Current active season: <span className="text-amber-400">{currentSeason || '— set one below —'}</span>
              </h3>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'} mt-1`}>
                Seasons in OmniLeagues are year labels like <code className="font-mono">2025-2026</code> or <code className="font-mono">2026-2027</code>. This page is the
                end-of-season handover: archive a finished season's rosters so
                you can recall them later, then optionally clone the team
                structure forward into the next year so the AD doesn't have
                to rebuild from scratch.
              </p>
              <ul className={`text-sm mt-2 space-y-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                <li>• <strong>Archive Current Season</strong> — snapshot every team's roster with a date stamp; optionally clear active rosters.</li>
                <li>• <strong>Start New Season from Archive</strong> — copy a stored snapshot into a new year label (e.g. 2025-2026 &rarr; 2026-2027).</li>
              </ul>
              <p className={`text-xs mt-2 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                Teams, pricing, and age eligibility live in <strong>Settings &rarr; Teams &amp; Costs</strong>; this tab is strictly for season transitions.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h4 className={`text-sm font-semibold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
            Archived Seasons {archives.length > 0 && <span className="opacity-60">({archives.length})</span>}
          </h4>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => { setShowArchive(true); setResult(null); setSeasonLabel(''); }} data-testid="archive-season-btn">
            <Archive className="h-4 w-4 mr-1" />Archive Current Season
          </Button>
          {archives.length > 0 && (
            <Button size="sm" variant="outline" className={isDark ? 'border-slate-600 text-slate-300' : ''} onClick={() => { setShowCopy(true); setResult(null); setNewSeason(''); }} data-testid="copy-season-btn">
              <Copy className="h-4 w-4 mr-1" />Start New Season from Archive
            </Button>
          )}
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setShowFinalize(true); setResult(null); setFinalizeSport(''); setFinalizeSeason(currentSeason || ''); setFinalizePrior(''); }} data-testid="finalize-season-btn">
            <Send className="h-4 w-4 mr-1" />Finalize Season → WordPress
          </Button>
        </div>
      </div>

      {finalizeLog.length > 0 && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'} data-testid="finalize-log-card">
          <CardHeader className="pb-2">
            <CardTitle className={`flex items-center gap-2 text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Trophy className="h-4 w-4 text-emerald-500" />Recent Finalize Runs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {finalizeLog.slice(0, 5).map(row => (
              <div key={row.id} className={`text-xs flex flex-wrap items-center gap-2 px-2 py-1 rounded ${isDark ? 'bg-slate-900/50' : 'bg-slate-50'}`} data-testid={`finalize-log-${row.id}`}>
                <Badge className={row.state === 'succeeded' ? 'bg-emerald-600 text-white' : row.state === 'partial' ? 'bg-amber-600 text-white' : 'bg-red-600 text-white'}>{row.state}</Badge>
                <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                  <strong>{row.sport_name || row.sport_id}</strong> · {row.season_slug}
                  {row.prior_season_slug && <> · wiped {row.prior_season_slug}</>}
                </span>
                <span className={`${isDark ? 'text-slate-500' : 'text-slate-400'} ml-auto`}>
                  {new Date(row.executed_at).toLocaleString()} · pushed {row.wp_pushed_counts?.players ?? 0}, wiped {row.wp_pre_wipe_counts?.players ?? 0}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
      ) : archives.length === 0 ? (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardContent className="p-8 text-center">
            <Archive className={`h-12 w-12 mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
            <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No archived seasons yet. Archive your current season when it ends.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {archives.map(a => (
            <Card key={a.id} className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar className={`h-5 w-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                  <div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{a.season}</p>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {a.team_count} teams, {a.roster_count} roster entries · Archived {new Date(a.archived_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <Badge className={isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-100 text-slate-600'}>{a.id.slice(0, 8)}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Archive Dialog */}
      <Dialog open={showArchive} onOpenChange={setShowArchive}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Archive className="h-5 w-5 text-amber-500" />Archive Current Season
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className={`rounded-md p-3 text-sm ${isDark ? 'bg-amber-900/20 border border-amber-800 text-amber-300' : 'bg-amber-50 border border-amber-200 text-amber-800'}`}>
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              This saves a snapshot of all team rosters. Optionally clears active rosters for a fresh start.
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Season Label</label>
              <Input value={seasonLabel} onChange={e => setSeasonLabel(e.target.value)} placeholder="e.g., 2025-2026" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="season-label-input" />
            </div>
            <label className={`flex items-center gap-2 text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              <input type="checkbox" checked={clearRosters} onChange={e => setClearRosters(e.target.checked)} data-testid="clear-rosters-checkbox" />
              Clear all rosters after archiving
            </label>
            {result && (
              <div className={`rounded-md p-3 text-sm ${result.type === 'success' ? (isDark ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-50 text-emerald-800') : (isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-800')}`} data-testid="archive-result">
                {result.type === 'success' ? <CheckCircle2 className="h-4 w-4 inline mr-1" /> : <AlertTriangle className="h-4 w-4 inline mr-1" />}
                {result.message}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowArchive(false)}>Cancel</Button>
              <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleArchive} disabled={actionLoading || !seasonLabel.trim()} data-testid="archive-confirm-btn">
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Archive Season'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Copy Season Dialog */}
      <Dialog open={showCopy} onOpenChange={setShowCopy}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Copy className="h-5 w-5 text-cyan-500" />Start New Season
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Source Archive</label>
              <select
                value={selectedArchive || ''}
                onChange={e => setSelectedArchive(e.target.value)}
                className={`w-full rounded-md border p-2 text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`}
                data-testid="source-archive-select"
              >
                <option value="">Select an archived season...</option>
                {archives.map(a => <option key={a.id} value={a.id}>{a.season} ({a.team_count} teams)</option>)}
              </select>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>New Season Label</label>
              <Input value={newSeason} onChange={e => setNewSeason(e.target.value)} placeholder="e.g., 2026-2027" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="new-season-input" />
            </div>
            {result && (
              <div className={`rounded-md p-3 text-sm ${result.type === 'success' ? (isDark ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-50 text-emerald-800') : (isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-800')}`} data-testid="copy-result">
                {result.message}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowCopy(false)}>Cancel</Button>
              <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" onClick={handleCopy} disabled={actionLoading || !selectedArchive || !newSeason.trim()} data-testid="copy-confirm-btn">
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Season'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Finalize Season → WordPress Dialog */}
      <Dialog open={showFinalize} onOpenChange={setShowFinalize}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700 max-w-xl' : 'bg-white border-slate-200 max-w-xl'}>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Send className="h-5 w-5 text-emerald-500" />Finalize Season → WordPress
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className={`rounded-md p-3 text-sm ${isDark ? 'bg-amber-900/20 border border-amber-800 text-amber-300' : 'bg-amber-50 border border-amber-200 text-amber-800'}`}>
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              This deletes the selected sport's prior-season player rows on WordPress and republishes
              the current OL roster. <strong>Players without a current team (cuts / unassigned) are
              kept in OL but excluded from WP.</strong> AD-only.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Sport</label>
                <select
                  value={finalizeSport}
                  onChange={e => setFinalizeSport(e.target.value)}
                  className={`w-full rounded-md border p-2 text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`}
                  data-testid="finalize-sport-select"
                >
                  <option value="">— Select a sport —</option>
                  {sports.map(s => <option key={s.id} value={s.id}>{s.name || s.label || s.slug}</option>)}
                </select>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>New Active Season</label>
                <Input value={finalizeSeason} onChange={e => setFinalizeSeason(e.target.value)} placeholder="e.g., 2026-2027" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="finalize-season-input" />
              </div>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>Prior Season to Wipe on WP <span className="opacity-60">(optional — defaults to "everything except the new season")</span></label>
              <Input value={finalizePrior} onChange={e => setFinalizePrior(e.target.value)} placeholder="e.g., 2025-2026" className={isDark ? 'bg-slate-700 border-slate-600 text-white' : ''} data-testid="finalize-prior-input" />
            </div>
            {finalizePreview && !finalizePreview.error && (
              <div className={`rounded-md p-3 text-sm space-y-1 ${isDark ? 'bg-slate-900/60 text-slate-200' : 'bg-slate-100 text-slate-700'}`} data-testid="finalize-preview">
                <div className="font-semibold flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-emerald-500" />Preview — {finalizePreview.sport?.name}
                </div>
                <div>SWS teams: <strong>{finalizePreview.sws_team_count}</strong></div>
                <div>Players to publish (have a current team): <strong className="text-emerald-400">{finalizePreview.publish_player_count}</strong></div>
                <div>Players to wipe on WP ({finalizePreview.prior_season_slug || `≠ ${finalizePreview.season_slug}`}): <strong className="text-red-400">{finalizePreview.wipe_player_count}</strong></div>
                <div>Schedules for <code className="font-mono">{finalizePreview.season_slug}</code>: <strong className="text-cyan-400">{finalizePreview.schedules_active ?? 0}</strong>{finalizePreview.prior_season_slug && (
                  <> · prior <code className="font-mono">{finalizePreview.prior_season_slug}</code>: <strong className="text-slate-400">{finalizePreview.schedules_prior ?? 0}</strong></>
                )}</div>
                <div className="opacity-70">Cuts / unassigned kept in OL: {finalizePreview.cut_or_unassigned_in_ol}</div>
              </div>
            )}
            {finalizePreview?.error && (
              <div className={`rounded-md p-3 text-sm ${isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-800'}`}>
                <AlertTriangle className="h-4 w-4 inline mr-1" />{finalizePreview.error}
              </div>
            )}
            {result && (
              <div className={`rounded-md p-3 text-sm ${result.type === 'success' ? (isDark ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-50 text-emerald-800') : (isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-800')}`} data-testid="finalize-result">
                {result.type === 'success' ? <CheckCircle2 className="h-4 w-4 inline mr-1" /> : <AlertTriangle className="h-4 w-4 inline mr-1" />}
                {result.message}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowFinalize(false)}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleFinalize} disabled={actionLoading || !finalizeSport || !finalizeSeason.trim()} data-testid="finalize-confirm-btn">
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Finalize & Push to WP'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
